import { FC, createContext } from 'react';
import debounce from 'lodash.debounce';
import { css } from '@emotion/core';
import { Box, Flex } from '@rebass/emotion';
import { MenuConfigHookOut } from '@app/hooks/useMenuConfig/types';
import { FinderState, generateRoute } from './url';
import styles from './ProductFinder.styles';
import TopMenu from './TopMenu';
import ProductSearch from './ProductSearch';
import ProductSort from './ProductSort';
import { ContentPageHookOutput } from '../../hooks/useContentPage';
import TermsBox from '../TermsBox';
import { TermsItem } from '../TermsBox/TermsBox';
import ProductListWrapper from './ProductListWrapper';
import FeaturedProducts from './FeaturedProducts';
import CatalogMeta from './CatalogMeta';
import SeamlessSection from './SeamlessSection';
import { PRODUCT_FINDER_SEGMENT } from '../../routes/routes';

interface ProductFinderProps
  extends Omit<MenuConfigHookOut, 'loading'>,
    ContentPageHookOutput {
  onSearch?: (newState: FinderState) => void;
}

const defaultSearchState: FinderState = {
  values: {},
};

export const SearchContext = createContext(defaultSearchState);

const SEARCH_DEBOUNCE_TIME = 300;

const debounced = debounce(
  (fn, ...params) => fn(...params),
  SEARCH_DEBOUNCE_TIME
);

const ProductFinder: FC<ProductFinderProps> = ({
  searchState,
  menu,
  menuSlugs,
  isPageProtected,
  sortingValues,
  productsCondition,
  language,
  contentPageSections,
  onSearch = () => {},
}) => {
  const debouncedOnSearch = (...args: unknown[]) =>
    debounced(onSearch, ...args);

  const getTermsPayload = (slugID: string): TermsItem | null => {
    const targetSlug = menu.find(({ slug }) => slug === slugID) || null;
    return targetSlug && targetSlug.terms;
  };

  const topMenuSlug = menuSlugs[0];
  const terms = getTermsPayload(topMenuSlug);

  const isSearching = typeof searchState.searching !== 'undefined';
  const isContentPage =
    !isSearching && contentPageSections && contentPageSections.length > 0;

  const category = `/${generateRoute([PRODUCT_FINDER_SEGMENT, ...menuSlugs])}/`;

  return (
    <SearchContext.Provider value={searchState}>
      <CatalogMeta menu={menu} menuSlugs={menuSlugs} />
      <TopMenu config={menu} value={menuSlugs} />
      <div
        css={css`
          background: white;
        `}
      >
        <Box css={styles.wrapper}>
          <Flex css={styles.header} justifyContent="flex-end">
            <Box
              css={css`
                flex: 1;
              `}
            >
              <ProductSearch
                searchState={searchState}
                onSearch={debouncedOnSearch}
              />
            </Box>
            <Box css={styles.productSort}>
              {sortingValues && !isContentPage ? (
                <ProductSort
                  onChange={onSearch}
                  sortingValues={sortingValues}
                  searchState={searchState}
                />
              ) : null}
            </Box>
          </Flex>
        </Box>
      </div>

      {isContentPage &&
        contentPageSections.map((section, index) => (
          <Box key={index} p={2} mb={2}>
            <Box
              data-testid="product-finder-content-section"
              css={styles.wrapper}
            >
              <Box css={styles.contentWrapper}>
                <FeaturedProducts {...section} />
              </Box>
            </Box>
          </Box>
        ))}

      {(isSearching || !contentPageSections) && (
        <Box p={2}>
          <Box css={styles.wrapper}>
            <Box css={styles.contentWrapper}>
              <Box>
                <ProductListWrapper
                  isPageProtected={!!isPageProtected}
                  condition={productsCondition}
                  language={language}
                  searchState={searchState}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <SeamlessSection category={category} />

      {terms && <TermsBox title={terms.title} body={terms.body} />}
    </SearchContext.Provider>
  );
};

export default ProductFinder;
